/**
 * Remove Object screen (remove_object tool).
 *
 * Flow: 1 Add photo → paint over what to remove (Brush) or let the app find
 * overlaid text (Remove Text) → Remove. The user never types a prompt: the
 * server owns the wording. In Brush mode the painted area is exported as a
 * black/white PNG mask, uploaded on Generate, and sent as `options.maskFileId`;
 * the server pastes only that area back onto the original photo.
 *
 * Painting happens on a zoomable stage (pinch / wheel / ± buttons, Move tool),
 * because the thing to remove is often small and far away (a person in the
 * background). Zoom is a view transform only; strokes are stored in photo
 * (mask) coordinates, so they stay put while zooming.
 * The price is data (GET /credits/pricing).
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import { SAMPLE_PHOTOS } from '../content/samplePhotos.js';
import type { CreditPricingConfig, JobOptions, UploadFileMeta } from '@loomia/shared';

const TOOL = 'remove_object' as const;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;

/** Mask size: long edge up to 1536 px, but never a short edge under 256 px (the upload minimum). */
const MASK_LONG_EDGE = 1536;
const MASK_MIN_SHORT = 256;
const MAX_ZOOM = 8;
const MAX_STAGE_VH = 0.62;
const BRUSH_MIN = 10;
const BRUSH_MAX = 90;
const BRUSH_DEFAULT = 30;

type Mode = 'brush' | 'text';

interface Photo {
  status: 'empty' | 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  error: string | null;
  /** Natural size of the (upright) photo; 0 until the preview has loaded. */
  nw: number;
  nh: number;
}

/** A stroke in mask pixels. A single point is a dot. */
interface Stroke {
  width: number;
  pts: Array<[number, number]>;
}

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function maskSize(nw: number, nh: number): { w: number; h: number } {
  const long = Math.max(nw, nh);
  const short = Math.min(nw, nh);
  const scale = Math.min(1, Math.max(MASK_LONG_EDGE / long, MASK_MIN_SHORT / short));
  return { w: Math.max(1, Math.round(nw * scale)), h: Math.max(1, Math.round(nh * scale)) };
}

export function renderRemoveObject(root: HTMLElement, _query: URLSearchParams): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="co"><div class="co-skel"></div></div>';
    const [pricingR] = await Promise.allSettled([api.getPricing(), refreshCredits().catch(() => null)]);
    if (!alive()) return;
    if (pricingR.status === 'rejected') {
      root.innerHTML = `
        <div class="co"><div class="co-state">${icon('cloud_off')}
          <p>Couldn't load Remove Object. Check your connection.</p>
          <button class="btn btn-secondary" data-act="retry">Try again</button></div></div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    mount(pricingR.value);
  };

  const mount = (pricing: CreditPricingConfig): void => {
    let mode: Mode = 'brush';
    let generating = false;
    let photo: Photo = { status: 'empty', progress: 0, previewUrl: null, fileId: null, error: null, nw: 0, nh: 0 };

    // Painting state (photo/mask coordinates).
    let strokes: Stroke[] = [];
    let brush = BRUSH_DEFAULT; // screen px (diameter)
    let moveTool = false;

    // View state: stage size, view (photo) size at zoom 1, current transform.
    let stageW = 0;
    let stageH = 0;
    let viewW = 0;
    let viewH = 0;
    let zoom = 1;
    let px = 0;
    let py = 0;

    const price = (): number => pricing.perTool[TOOL]?.credits ?? 0;

    const missing = (): string | null => {
      if (photo.status === 'uploading') return 'Uploading…';
      if (photo.status !== 'ready') return 'Add your photo first';
      if (mode === 'brush' && strokes.length === 0) return 'Paint what to remove';
      return null;
    };

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string): string =>
      `<div class="co-step"><span class="co-num">${n}</span><h2>${title}</h2></div>`;

    const modesHtml = (): string => {
      const off = photo.status === 'empty' || photo.status === 'error';
      const opt = (m: Mode, label: string, ic: string): string =>
        `<button class="co-seg-opt${m === mode ? ' selected' : ''}" role="radio" aria-checked="${m === mode}" data-mode="${m}" ${off ? 'disabled' : ''}>${icon(ic)} ${label}</button>`;
      return `<div class="co-seg ro-modes" role="radiogroup" aria-label="Removal mode">${opt('brush', 'Brush', 'brush')}${opt('text', 'Remove Text', 'text_fields')}</div>`;
    };

    const dropHtml = (): string => {
      const samples =
        SAMPLE_PHOTOS.length === 0
          ? ''
          : `<div class="co-samples"><p class="co-sub">Try these photos</p><div class="co-samples-row">${SAMPLE_PHOTOS.map(
              (s, i) => `<button class="co-sample" data-sample="${i}" aria-label="${escapeHtml(s.label)}"><img src="${escapeHtml(s.url)}" alt="" loading="lazy" /></button>`,
            ).join('')}</div></div>`;
      return `
        <div class="co-photo-row${SAMPLE_PHOTOS.length === 0 ? ' no-samples' : ''}">
          <button class="co-drop" data-act="pick">
            ${icon('add_photo_alternate')}<strong>Add your photo</strong><span>JPG, PNG or WebP · up to 25 MB</span>
          </button>
          ${samples}
        </div>
        ${photo.status === 'error' ? `<p class="co-warn">${escapeHtml(photo.error ?? 'Upload failed')}</p>` : ''}`;
    };

    const stageHtml = (): string => `
      <div class="ro-stage-wrap">
        <div class="ro-stage${moveTool ? ' is-move' : ''}${mode === 'text' ? ' is-text' : ''}" id="ro-stage">
          <div class="ro-view" id="ro-view">
            <img id="ro-img" src="${escapeHtml(photo.previewUrl ?? '')}" alt="Your photo" draggable="false" />
            <canvas class="ro-paint" id="ro-paint"></canvas>
          </div>
          <div class="ro-ring" id="ro-ring" hidden></div>
          ${mode === 'brush' ? `<span class="ro-hint">Paint over what you want to remove</span>` : ''}
          <div class="ro-zoom">
            <button data-act="zoom-in" aria-label="Zoom in">${icon('zoom_in')}</button>
            <button data-act="zoom-out" aria-label="Zoom out">${icon('zoom_out')}</button>
            ${mode === 'brush' ? `<button data-act="move" class="${moveTool ? 'on' : ''}" aria-label="Move photo" aria-pressed="${moveTool}">${icon('pan_tool')}</button>` : ''}
          </div>
          ${photo.status === 'uploading' ? `<div class="co-bar"><span style="width:${Math.round(photo.progress * 100)}%"></span></div>` : ''}
        </div>
      </div>`;

    const toolsHtml = (): string =>
      mode !== 'brush'
        ? `<div class="ro-change"><button class="co-link" data-act="pick">Change photo</button></div>`
        : `
        <div class="ro-size">
          <span>Brush size</span>
          <input type="range" id="ro-size" min="${BRUSH_MIN}" max="${BRUSH_MAX}" value="${brush}" aria-label="Brush size" />
          <i class="ro-size-dot" id="ro-size-dot" style="width:${Math.round(brush / 2.5)}px;height:${Math.round(brush / 2.5)}px"></i>
        </div>
        <div class="ro-tools">
          <button class="ro-tool" data-act="undo" ${strokes.length === 0 ? 'disabled' : ''}>${icon('undo')} Undo</button>
          <button class="ro-tool" data-act="clear" ${strokes.length === 0 ? 'disabled' : ''}>${icon('delete')} Clear</button>
          <button class="ro-tool" data-act="pick">Change photo</button>
        </div>
        <p class="co-sub ro-tip">Pinch to zoom in on small details. Include the shadow too.</p>`;

    const textInfoHtml = (): string => `
      <div class="ro-info">${icon('text_fields')}<p>We'll detect and remove text, dates and captions automatically.</p></div>
      <p class="co-note">${icon('info')}<span>Only use photos you own or have permission to edit.</span></p>`;

    const footerHtml = (): string => {
      const c = price();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      const btn =
        short > 0 && !why
          ? '<button class="btn btn-primary btn-block" data-act="buy">Get credits</button>'
          : `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
              generating ? 'Starting…' : why ? escapeHtml(why) : `Remove ${icon('toll', { fill: true })} ${c}`
            }</button>`;
      return `
        <div class="co-total${short > 0 ? ' is-short' : ''}">
          ${
            short > 0
              ? `<span class="co-warn">${icon('warning')} You need ${icon('toll', { fill: true })} ${c} · You have ${icon('toll', { fill: true })} ${bal ?? 0}</span>`
              : `<span>Total</span><strong>${icon('toll', { fill: true })} ${c}</strong><small>Failed runs are refunded automatically</small>`
          }
        </div>
        ${btn}`;
    };

    const paint = (): void => {
      const hasPhoto = photo.status === 'ready' || photo.status === 'uploading';
      root.innerHTML = `
        <div class="co ro">
          <header class="co-head">
            <button class="co-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <div class="co-head-text"><h1>Remove Object</h1><p>Easily remove unwanted objects, people or text from your photos with AI.</p></div>
            <a class="co-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          ${step(1, hasPhoto ? 'Your photo' : 'Upload your photo')}
          ${hasPhoto ? stageHtml() : dropHtml()}
          ${step(2, 'Choose how to remove')}
          ${modesHtml()}
          ${hasPhoto ? (mode === 'brush' ? toolsHtml() : `${toolsHtml()}${textInfoHtml()}`) : ''}
          <footer class="co-foot" id="co-foot">${footerHtml()}</footer>
          <input type="file" id="co-file" accept="${ACCEPTED_TYPES.join(',')}" hidden />
        </div>`;
      if (hasPhoto) setupStage();
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#co-foot');
      if (f) f.innerHTML = footerHtml();
    };

    // -- Stage: layout, drawing, gestures ----------------------------------

    const el = <T extends HTMLElement>(id: string): T | null => root.querySelector<T>(`#${id}`);

    const clampPan = (): void => {
      const sw = viewW * zoom;
      const sh = viewH * zoom;
      px = sw <= stageW ? (stageW - sw) / 2 : clamp(px, stageW - sw, 0);
      py = sh <= stageH ? (stageH - sh) / 2 : clamp(py, stageH - sh, 0);
    };

    const applyTransform = (): void => {
      clampPan();
      const view = el('ro-view');
      if (view) view.style.transform = `translate(${px}px, ${py}px) scale(${zoom})`;
    };

    const zoomAt = (cx: number, cy: number, next: number): void => {
      const z = clamp(next, 1, MAX_ZOOM);
      // Keep the photo point under (cx, cy) where it is.
      const fx = (cx - px) / zoom;
      const fy = (cy - py) / zoom;
      zoom = z;
      px = cx - fx * zoom;
      py = cy - fy * zoom;
      applyTransform();
    };

    const redraw = (): void => {
      const canvas = el<HTMLCanvasElement>('ro-paint');
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of strokes) drawStroke(ctx, s, '#ff2d55', null);
    };

    const drawStroke = (ctx: CanvasRenderingContext2D, s: Stroke, color: string, from: number | null): void => {
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = s.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const pts = s.pts;
      if (pts.length === 1) {
        const [x, y] = pts[0]!;
        ctx.beginPath();
        ctx.arc(x, y, s.width / 2, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      ctx.beginPath();
      const start = from === null ? 0 : Math.max(0, from - 1);
      ctx.moveTo(pts[start]![0], pts[start]![1]);
      for (let i = start + 1; i < pts.length; i++) ctx.lineTo(pts[i]![0], pts[i]![1]);
      ctx.stroke();
    };

    /** Black/white PNG at mask resolution: white = remove. */
    const exportMask = (): Promise<Blob> => {
      const { w, h } = maskSize(photo.nw, photo.nh);
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) return Promise.reject(new Error('Canvas is not available.'));
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      for (const s of strokes) drawStroke(ctx, s, '#fff', null);
      return new Promise((resolve, reject) =>
        c.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not export the mask.'))), 'image/png'),
      );
    };

    const layout = (): void => {
      const stage = el('ro-stage');
      const view = el('ro-view');
      const canvas = el<HTMLCanvasElement>('ro-paint');
      if (!stage || !view || !canvas || !photo.nw || !photo.nh) return;
      const wrapW = stage.parentElement?.clientWidth ?? stage.clientWidth;
      const ratio = photo.nh / photo.nw;
      stageW = wrapW;
      stageH = Math.min(wrapW * ratio, Math.round(window.innerHeight * MAX_STAGE_VH));
      stage.style.width = `${stageW}px`;
      stage.style.height = `${stageH}px`;
      // Photo fitted ("contain") into the stage at zoom 1.
      viewW = Math.min(stageW, stageH / ratio);
      viewH = viewW * ratio;
      view.style.width = `${viewW}px`;
      view.style.height = `${viewH}px`;
      const m = maskSize(photo.nw, photo.nh);
      if (canvas.width !== m.w || canvas.height !== m.h) {
        canvas.width = m.w;
        canvas.height = m.h;
      }
      zoom = 1;
      px = 0;
      py = 0;
      applyTransform();
      redraw();
    };

    /** Stage point (css px inside the stage) → photo/mask coordinates. */
    const toMask = (clientX: number, clientY: number): [number, number] => {
      const stage = el('ro-stage');
      const r = stage?.getBoundingClientRect();
      const m = maskSize(photo.nw, photo.nh);
      const sx = clientX - (r?.left ?? 0);
      const sy = clientY - (r?.top ?? 0);
      return [clamp(((sx - px) / zoom / viewW) * m.w, 0, m.w), clamp(((sy - py) / zoom / viewH) * m.h, 0, m.h)];
    };

    /** Brush width in mask px for the current zoom (the brush keeps its size on screen). */
    const brushInMask = (): number => {
      const m = maskSize(photo.nw, photo.nh);
      return (brush * (m.w / viewW)) / zoom;
    };

    const setupStage = (): void => {
      const stage = el('ro-stage');
      const img = el<HTMLImageElement>('ro-img');
      const ring = el('ro-ring');
      if (!stage || !img) return;

      const ready = (): void => {
        if (!alive()) return;
        photo.nw = img.naturalWidth;
        photo.nh = img.naturalHeight;
        layout();
      };
      if (img.complete && img.naturalWidth) ready();
      else img.addEventListener('load', ready, { once: true });

      // Pointers on the stage: one finger paints (or moves), two fingers pinch + pan.
      const pointers = new Map<number, { x: number; y: number }>();
      let current: Stroke | null = null;
      let drawnTo = 0;
      let pinch: { dist: number; zoom: number; fx: number; fy: number } | null = null;
      let panFrom: { x: number; y: number; px: number; py: number } | null = null;

      const stagePos = (e: PointerEvent): { x: number; y: number } => {
        const r = stage.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      };
      const moveRing = (e: PointerEvent): void => {
        if (!ring) return;
        const show = e.pointerType === 'mouse' && mode === 'brush' && !moveTool;
        ring.hidden = !show;
        if (show) {
          const p = stagePos(e);
          ring.style.width = ring.style.height = `${brush}px`;
          ring.style.transform = `translate(${p.x - brush / 2}px, ${p.y - brush / 2}px)`;
        }
      };
      const paintCtx = (): CanvasRenderingContext2D | null => el<HTMLCanvasElement>('ro-paint')?.getContext('2d') ?? null;
      const refreshTools = (): void => {
        root.querySelectorAll<HTMLButtonElement>('[data-act="undo"],[data-act="clear"]').forEach((b) => {
          b.disabled = strokes.length === 0;
        });
        paintFooter();
      };

      stage.onpointerdown = (e) => {
        if (!photo.nw) return;
        // The zoom buttons live inside the stage: let them handle their own clicks.
        if ((e.target as HTMLElement).closest('.ro-zoom')) return;
        stage.setPointerCapture(e.pointerId);
        pointers.set(e.pointerId, stagePos(e));
        if (pointers.size === 2) {
          // A second finger: drop the stroke that just started and switch to pinch.
          if (current) {
            strokes.pop();
            current = null;
            redraw();
          }
          const [a, b] = [...pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom, fx: (mx - px) / zoom, fy: (my - py) / zoom };
          return;
        }
        if (pointers.size > 2) return;
        if (mode === 'text' || moveTool) {
          const p = stagePos(e);
          panFrom = { x: p.x, y: p.y, px, py };
          return;
        }
        const [x, y] = toMask(e.clientX, e.clientY);
        current = { width: brushInMask(), pts: [[x, y]] };
        strokes.push(current);
        drawnTo = 0;
        const ctx = paintCtx();
        if (ctx) drawStroke(ctx, current, '#ff2d55', null);
        refreshTools();
      };

      stage.onpointermove = (e) => {
        moveRing(e);
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, stagePos(e));
        if (pinch && pointers.size >= 2) {
          const [a, b] = [...pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          const next = clamp(pinch.zoom * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.dist), 1, MAX_ZOOM);
          zoom = next;
          px = mx - pinch.fx * zoom;
          py = my - pinch.fy * zoom;
          applyTransform();
          return;
        }
        if (panFrom) {
          const p = stagePos(e);
          px = panFrom.px + (p.x - panFrom.x);
          py = panFrom.py + (p.y - panFrom.y);
          applyTransform();
          return;
        }
        if (current) {
          // Coalesced events keep fast strokes smooth.
          const evs = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
          for (const ev of evs.length ? evs : [e]) current.pts.push(toMask(ev.clientX, ev.clientY));
          const ctx = paintCtx();
          if (ctx) drawStroke(ctx, current, '#ff2d55', drawnTo);
          drawnTo = current.pts.length - 1;
        }
      };

      const end = (e: PointerEvent): void => {
        pointers.delete(e.pointerId);
        if (pointers.size < 2) pinch = null;
        if (pointers.size === 0) {
          panFrom = null;
          if (current) {
            current = null;
            refreshTools();
          }
        }
      };
      stage.onpointerup = end;
      stage.onpointercancel = end;
      stage.onpointerleave = () => {
        if (ring) ring.hidden = true;
      };
      stage.onwheel = (e) => {
        e.preventDefault();
        const r = stage.getBoundingClientRect();
        zoomAt(e.clientX - r.left, e.clientY - r.top, zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
      };
    };

    // -- Upload -----------------------------------------------------------

    const upload = async (file: File): Promise<void> => {
      if (!requireSignIn('Sign in to add your photo.')) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > CLIENT_MAX_BYTES) {
        toast('That image is too large. Please pick one under 25 MB.');
        return;
      }
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
      strokes = [];
      photo = { status: 'uploading', progress: 0, previewUrl: URL.createObjectURL(file), fileId: null, error: null, nw: 0, nh: 0 };
      const mine = photo;
      paint();
      try {
        const req = await api.requestUpload({
          purpose: 'generation_input',
          contentType: file.type,
          contentLength: file.size,
        });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || photo !== mine) return;
          mine.progress = r;
          const bar = root.querySelector<HTMLElement>('.co-bar span');
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || photo !== mine) return;
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        mine.status = 'ready';
        mine.fileId = meta.id;
        root.querySelector('.co-bar')?.remove();
        paintFooter();
      } catch (err) {
        if (!alive() || photo !== mine) return;
        mine.status = 'error';
        mine.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
        paint();
      }
    };

    // -- Generate ---------------------------------------------------------

    const uploadMask = async (): Promise<string> => {
      const blob = await exportMask();
      const req = await api.requestUpload({ purpose: 'generation_input', contentType: 'image/png', contentLength: blob.size });
      await api.putUploadBytes(req.uploadUrl, blob, req.uploadHeaders, () => undefined);
      const meta = await api.completeUpload(req.fileId);
      if (meta.status !== 'ready') throw new Error(meta.error || 'Could not prepare your selection.');
      return meta.id;
    };

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why === 'Paint what to remove' ? 'Paint over what you want to remove first.' : why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = price();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const options: JobOptions =
          mode === 'text' ? { removeMode: 'text' } : { removeMode: 'brush', maskFileId: await uploadMask() };
        const { job } = await api.submitJob({
          tool: TOOL,
          inputFileIds: photo.fileId ? [photo.fileId] : [],
          options,
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated) -----------------------------------------------

    root.onclick = (e) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-mode],[data-sample]');
      if (!target) return;
      const d = target.dataset;
      if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'pick') {
        if (requireSignIn('Sign in to add your photo.')) {
          withUploadGuide('remove_object', () => root.querySelector<HTMLInputElement>('#co-file')?.click());
        }
      } else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.act === 'undo') {
        strokes.pop();
        redraw();
        paintFooter();
        root.querySelectorAll<HTMLButtonElement>('[data-act="undo"],[data-act="clear"]').forEach((b) => {
          b.disabled = strokes.length === 0;
        });
      } else if (d.act === 'clear') {
        strokes = [];
        redraw();
        paintFooter();
        root.querySelectorAll<HTMLButtonElement>('[data-act="undo"],[data-act="clear"]').forEach((b) => {
          b.disabled = true;
        });
      } else if (d.act === 'move') {
        moveTool = !moveTool;
        target.classList.toggle('on', moveTool);
        target.setAttribute('aria-pressed', String(moveTool));
        el('ro-stage')?.classList.toggle('is-move', moveTool);
      } else if (d.act === 'zoom-in' || d.act === 'zoom-out') {
        zoomAt(stageW / 2, stageH / 2, zoom * (d.act === 'zoom-in' ? 1.5 : 1 / 1.5));
      } else if (d.mode) {
        const next: Mode = d.mode === 'text' ? 'text' : 'brush';
        if (next !== mode) {
          mode = next;
          moveTool = false;
          paint();
        }
      } else if (d.sample) {
        const s = SAMPLE_PHOTOS[Number(d.sample)];
        if (s) {
          void fetch(s.url)
            .then((r) => r.blob())
            .then((b) => upload(new File([b], 'sample.webp', { type: b.type || 'image/webp' })))
            .catch(() => toast("Couldn't load that sample photo."));
        }
      }
    };

    root.oninput = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'ro-size') return;
      brush = clamp(Number(input.value) || BRUSH_DEFAULT, BRUSH_MIN, BRUSH_MAX);
      const dot = el('ro-size-dot');
      if (dot) dot.style.width = dot.style.height = `${Math.round(brush / 2.5)}px`;
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'co-file') return;
      const f = input.files?.[0];
      input.value = '';
      if (f) void upload(f);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    const onResize = (): void => {
      if (alive() && photo.nw) layout();
    };
    window.addEventListener('resize', onResize);
    prevCleanup = () => {
      unsub();
      window.removeEventListener('resize', onResize);
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
    };

    paint();
  };

  void load();
}
